import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  View,
  Text,
  Pressable,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Platform,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { useTheme, useThemeColors, GTFonts } from '../../theme';
import { s, fs, iconSize, v } from '../../utils/libs';

type CalculatorVariant = 'page' | 'modal';
type MaterialCommunityIconName = React.ComponentProps<
  typeof MaterialCommunityIcons
>['name'];

type CalculatorProps = {
  variant?: CalculatorVariant;
  onSubmit?: (value: number, expression: string) => void;
  onCancel?: () => void;
  storageKey?: string; // custom key for multiple calculators if ever needed
};

type KeyDef = {
  key: string; // unique id
  label?: string; // text label
  icon?: MaterialCommunityIconName; // material-community icon name
  onPress?: () => void; // populated at runtime
  testID?: string;
  accessibilityLabel?: string;
  type: 'digit' | 'operator' | 'dot' | 'equals' | 'clear' | 'delete';
  value?: string; // appended raw token for input
};

const DEFAULT_STORAGE_KEY = 'calculator:v1';

const OPERATORS: Record<string, { prec: number; assoc: 'L' | 'R' }> = {
  '+': { prec: 1, assoc: 'L' },
  '-': { prec: 1, assoc: 'L' },
  '*': { prec: 2, assoc: 'L' },
  '/': { prec: 2, assoc: 'L' },
};

function tokenize(expression: string): string[] {
  const tokens: string[] = [];
  let current = '';
  for (let i = 0; i < expression.length; i++) {
    const ch = expression[i];
    if (/[0-9.]/.test(ch)) {
      current += ch;
    } else if (/[+\-*/()]/.test(ch)) {
      if (current) {
        tokens.push(current);
        current = '';
      }
      // unary minus handling: convert to (0 - x) form by inserting 0
      if (
        ch === '-' &&
        (tokens.length === 0 || /[+\-*/(]/.test(tokens[tokens.length - 1]))
      ) {
        tokens.push('0');
        tokens.push('-');
        continue;
      }
      tokens.push(ch);
    } else if (ch === ' ') {
      // skip
    } else {
      // ignore any unknown character
    }
  }
  if (current) tokens.push(current);
  return tokens;
}

function toRPN(tokens: string[]): string[] {
  const output: string[] = [];
  const stack: string[] = [];
  for (const token of tokens) {
    if (!isNaN(Number(token))) {
      output.push(token);
    } else if (token in OPERATORS) {
      const o1 = token;
      while (stack.length) {
        const o2 = stack[stack.length - 1];
        if (
          o2 in OPERATORS &&
          ((OPERATORS[o1].assoc === 'L' &&
            OPERATORS[o1].prec <= OPERATORS[o2].prec) ||
            (OPERATORS[o1].assoc === 'R' &&
              OPERATORS[o1].prec < OPERATORS[o2].prec))
        ) {
          output.push(stack.pop() as string);
        } else break;
      }
      stack.push(o1);
    } else if (token === '(') {
      stack.push(token);
    } else if (token === ')') {
      while (stack.length && stack[stack.length - 1] !== '(') {
        output.push(stack.pop() as string);
      }
      if (stack[stack.length - 1] === '(') stack.pop();
    }
  }
  while (stack.length) output.push(stack.pop() as string);
  return output;
}

function evalRPN(rpn: string[]): number {
  const stack: number[] = [];
  for (const token of rpn) {
    if (!isNaN(Number(token))) {
      stack.push(Number(token));
    } else if (token in OPERATORS) {
      const b = stack.pop();
      const a = stack.pop();
      if (a === undefined || b === undefined) return NaN;
      switch (token) {
        case '+':
          stack.push(a + b);
          break;
        case '-':
          stack.push(a - b);
          break;
        case '*':
          stack.push(a * b);
          break;
        case '/':
          stack.push(b === 0 ? NaN : a / b);
          break;
      }
    }
  }
  return stack.pop() ?? NaN;
}

function safeEvaluate(expression: string): number {
  try {
    const sanitized = expression.replace(/[^0-9+\-*/().]/g, '');
    const tokens = tokenize(sanitized);
    if (!tokens.length) return 0;
    const rpn = toRPN(tokens);
    const result = evalRPN(rpn);
    if (!isFinite(result)) return NaN;
    return result;
  } catch {
    return NaN;
  }
}

function isExpressionComplete(expr: string): boolean {
  const s = expr.trim();
  if (!s) return true;
  if (/[+\-*/(]$/.test(s)) return false;
  if (/\.$/.test(s)) return false;
  const opens = (s.match(/\(/g) || []).length;
  const closes = (s.match(/\)/g) || []).length;
  if (opens !== closes) return false;
  return true;
}

// format helpers for display only (do not mutate stored expression/result)
function formatNumberStringForDisplay(numStr: string): string {
  // preserve sign and decimals
  const isNegative = numStr.startsWith('-');
  const unsigned = isNegative ? numStr.slice(1) : numStr;
  const [intPartRaw, decimalPart] = unsigned.split('.');
  const intPart = intPartRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (
    (isNegative ? '-' : '') +
    intPart +
    (decimalPart !== undefined ? `.${decimalPart}` : '')
  );
}

function formatExpressionForDisplay(expr: string): string {
  // add commas to each numeric segment in the expression
  return expr.replace(/\d+(?:\.\d+)?/g, m => formatNumberStringForDisplay(m));
}

function formatResultForDisplay(value: number): string {
  if (!Number.isFinite(value)) return 'Error';
  // Avoid locale side-effects; format via string-based approach
  return formatNumberStringForDisplay(String(value));
}

// Animated key with scale + haptic feedback
const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

type AnimatedKeyProps = {
  onPress: () => void;
  style: any;
  hoverStyle?: any;
  children: React.ReactNode;
};

const AnimatedKey: React.FC<AnimatedKeyProps> = ({
  onPress,
  style,
  hoverStyle,
  children,
}) => {
  const scale = useRef(new Animated.Value(1)).current;
  const [isHovered, setIsHovered] = useState(false);

  const handlePressIn = useCallback(() => {
    // Vibration.vibrate(5);
    Animated.spring(scale, {
      toValue: 0.92,
      useNativeDriver: true,
      speed: 80,
      bounciness: 2,
    }).start();
  }, [scale]);

  const handlePressOut = useCallback(() => {
    Animated.spring(scale, {
      toValue: 1,
      useNativeDriver: true,
      speed: 50,
      bounciness: 4,
    }).start();
  }, [scale]);

  return (
    <AnimatedPressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onHoverIn={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
      onHoverOut={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
      style={[
        style,
        Platform.OS === 'web' && isHovered && hoverStyle,
        { transform: [{ scale }] },
      ]}>
      {children}
    </AnimatedPressable>
  );
};

export const Calculator: React.FC<CalculatorProps> = ({
  variant = 'page',
  onSubmit,
  onCancel,
  storageKey,
}) => {
  const { theme } = useTheme();
  const colors = useThemeColors();
  const STORAGE_KEY = storageKey || DEFAULT_STORAGE_KEY;
  const isPage = variant === 'page';

  const [expression, setExpression] = useState<string>('');
  const [result, setResult] = useState<number>(0);

  // Load persisted state once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!mounted) return;
        setExpression(String(parsed?.expression || ''));
        setResult(Number(parsed?.result || 0));
      } catch {}
    })();
    return () => {
      mounted = false;
    };
  }, [STORAGE_KEY]);

  // Persist on change (light debounce via ref)
  const persistTimer = useRef<null | ReturnType<typeof setTimeout>>(null);
  const persist = useCallback(
    (nextExpr: string, nextResult: number) => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
      persistTimer.current = setTimeout(() => {
        AsyncStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ expression: nextExpr, result: nextResult }),
        ).catch(() => {});
      }, 150);
    },
    [STORAGE_KEY],
  );

  // Update result as user types (only when expression is complete)
  useEffect(() => {
    const complete = isExpressionComplete(expression);
    if (complete) {
      const val = expression.trim() ? safeEvaluate(expression) : 0;
      if (Number.isFinite(val)) {
        setResult(val);
        persist(expression, val);
      } else {
        // keep last valid result
        persist(expression, result);
      }
    } else {
      // incomplete; persist but don't change result
      persist(expression, result);
    }
  }, [expression, result, persist]);

  const appendToken = useCallback((token: string) => {
    setExpression(prev => {
      if (token === '.') {
        const lastNumber = prev.split(/[+\-*/()]/).pop() || '';
        if (lastNumber.includes('.')) return prev; // prevent multiple dots in current number
      }
      if (/[+\-*/]/.test(token)) {
        if (prev === '' && token !== '-') return prev; // don't start with operator except unary minus
        // avoid duplicate operators (replace last)
        if (/[+\-*/]$/.test(prev)) return prev.slice(0, -1) + token;
      }
      return prev + token;
    });
  }, []);

  const handleClear = useCallback(() => {
    setExpression('');
    setResult(0);
    persist('', 0);
  }, [persist]);

  const handleDelete = useCallback(() => {
    setExpression(prev => prev.slice(0, -1));
  }, []);

  const handleEquals = useCallback(() => {
    if (!isExpressionComplete(expression)) return;
    const val = expression.trim() ? safeEvaluate(expression) : 0;
    setResult(Number.isFinite(val) ? val : NaN);
    if (onSubmit && Number.isFinite(val)) onSubmit(val, expression);
  }, [expression, onSubmit]);

  const keys: KeyDef[] = useMemo(
    () => [
      { key: '7', type: 'digit', value: '7' },
      { key: '8', type: 'digit', value: '8' },
      { key: '9', type: 'digit', value: '9' },
      { key: 'divide', type: 'operator', value: '/', icon: 'division' },
      { key: '4', type: 'digit', value: '4' },
      { key: '5', type: 'digit', value: '5' },
      { key: '6', type: 'digit', value: '6' },
      { key: 'multiply', type: 'operator', value: '*', icon: 'close' },
      { key: '1', type: 'digit', value: '1' },
      { key: '2', type: 'digit', value: '2' },
      { key: '3', type: 'digit', value: '3' },
      { key: 'minus', type: 'operator', value: '-', icon: 'minus' },
      { key: 'dot', type: 'dot', value: '.' },
      { key: '0', type: 'digit', value: '0' },
      { key: 'equals', type: 'equals', label: '=', value: '=' },
      { key: 'plus', type: 'operator', value: '+', icon: 'plus' },
    ],
    [],
  );

  const prettyExpression = useMemo(
    () => formatExpressionForDisplay(expression),
    [expression],
  );

  return (
    <View
      style={[
        styles.container,
        isPage ? styles.pageContainer : { padding: s(8) },
      ]}>
      <View
        style={[
          styles.displayContainer,
          isPage && styles.displayContainerPage,
        ]}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text
            numberOfLines={1}
            style={[
              styles.expression,
              isPage && styles.expressionPage,
              { color: colors.text },
            ]}>
            {prettyExpression || '0'}
          </Text>
          <TouchableOpacity
            onPress={() => {
              // Vibration.vibrate(5);
              handleDelete();
            }}
            accessibilityLabel="delete"
            style={styles.deleteBtn}>
            <MaterialCommunityIcons
              name="backspace-outline"
              size={iconSize(20)}
              color={colors.text}
            />
          </TouchableOpacity>
        </View>
        <Text
          numberOfLines={1}
          style={[
            styles.result,
            isPage && styles.resultPage,
            { color: Number.isFinite(result) ? colors.primary : colors.error },
          ]}>
          {Number.isFinite(result) ? formatResultForDisplay(result) : 'Error'}
        </Text>
        {isPage ? (
          <View style={styles.displayActionsRow}>
            <TouchableOpacity
              onPress={() => {
                // Vibration.vibrate(5);
                handleClear();
              }}
              style={styles.displayClearBtn}>
              <Text style={[styles.actionText, { color: theme.colors.error }]}>
                CLEAR
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}
      </View>

      <View style={[styles.grid, isPage && styles.gridPage]}>
        {keys.map(k => {
          const onPress = () => {
            switch (k.type) {
              case 'digit':
              case 'operator':
              case 'dot':
                appendToken(k.value || '');
                break;
              case 'equals':
                handleEquals();
                break;
              case 'clear':
                handleClear();
                break;
              case 'delete':
                handleDelete();
                break;
            }
          };
          const isOperator = k.type === 'operator' || k.type === 'equals';
          return (
            <AnimatedKey
              key={k.key}
              onPress={onPress}
              hoverStyle={styles.keyHovered}
              style={[
                styles.key,
                isPage && styles.keyPage,
                {
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.border,
                },
                isOperator && { backgroundColor: theme.colors.secondaryLight },
              ]}>
              {k.icon ? (
                <MaterialCommunityIcons
                  name={k.icon}
                  size={iconSize(isPage ? 24 : 20)}
                  color={theme.colors.text}
                />
              ) : (
                <Text style={[styles.keyText, { color: theme.colors.text }]}>
                  {k.label ?? k.value}
                </Text>
              )}
            </AnimatedKey>
          );
        })}
      </View>

      {variant === 'modal' ? (
        <View style={styles.actions}>
          <TouchableOpacity
            onPress={() => {
              // Vibration.vibrate(5);
              handleClear();
            }}
            style={styles.actionBtn}>
            <Text style={[styles.actionText, { color: theme.colors.link }]}>
              CLEAR
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => {
              // Vibration.vibrate(5);
              onCancel?.();
            }}
            style={styles.actionBtn}>
            <Text style={[styles.actionText, { color: colors.errorDark }]}>
              CANCEL
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  pageContainer: {
    flex: 1,
    // paddingHorizontal: s(10, 'width'),
    // paddingTop: v(4, 'height'),
    // paddingBottom: v(8, 'height'),
  },
  displayContainer: {
    backgroundColor: 'white',
    borderRadius: s(10),
    paddingHorizontal: s(12),
    paddingVertical: s(10),
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: '#e5e5e5',
  },
  displayContainerPage: {
    paddingVertical: s(10),
    // Give the input/output display more vertical space on native
    minHeight: Platform.OS === 'web' ? v(360) : v(220),
    justifyContent: 'space-between',
  },
  displayTop: {
    flexDirection: 'column',
  },
  expression: {
    flex: 1,
    fontFamily: GTFonts.regular,
    fontSize: fs(18),
    lineHeight: fs(22),
  },
  expressionPage: {
    fontSize: fs(22),
    lineHeight: fs(26),
  },
  deleteBtn: {
    padding: s(6),
  },
  result: {
    textAlign: 'right',
    fontFamily: GTFonts.semibold,
    fontSize: fs(22),
    lineHeight: fs(26),
    marginTop: v(16),
  },
  resultPage: {
    fontSize: fs(30),
    lineHeight: fs(34),
  },
  grid: {
    marginVertical: v(10),
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  gridPage: {
    flexGrow: 1,
    alignContent: Platform.OS === 'web' ? 'flex-start' : 'space-around',
    paddingTop: Platform.OS === 'web' ? v(20) : 0,
  },
  key: {
    width: '23.5%',
    marginBottom: v(10),
    paddingVertical: s(12),
    borderRadius: s(8),
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
  },
  keyHovered: {
    backgroundColor: 'rgba(21,96,189,0.12)',
  },
  keyPage: {
    // Shorter keys on page variant (native)
    paddingVertical: s(12),
    marginBottom: Platform.OS === 'web' ? v(20) : v(8),
  },
  keyText: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(23),
  },
  keyTextPage: {
    fontSize: fs(22),
  },
  actions: {
    marginTop: v(4),
    paddingHorizontal: s(4),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  displayActionsRow: {
    marginTop: v(8),
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  displayClearBtn: {
    paddingVertical: s(6),
    paddingHorizontal: s(8),
  },
  actionBtn: {
    paddingVertical: s(10),
    paddingHorizontal: s(12),
  },
  actionText: {
    fontFamily: GTFonts.semibold,
    fontSize: fs(14),
    letterSpacing: 0.3,
  },
});

export default Calculator;
